import {
	Output,
	Mp4OutputFormat,
	WavOutputFormat,
	AudioBufferSource,
} from 'mediabunny';
import {createMbTarget} from './targets';
import type {Composition} from '../composition/composition';
import {AudioClip} from '../clips/audio/audio';
import {VideoClip} from '../clips/video/video';
import {isScriptClip} from '../clips/script';
import {writeMixedAudioTrack, type AudioExportItem} from './audio.mixdown';
import { splitTimelineRangeBySpeakerMutes } from './script-speaker-mute';

type AudioEncoderInit = {
	bitrate?: number;
	format?: 'm4a' | 'wav';
	channels?: 1 | 2;
	sampleRate?: 44100 | 48000;
};

type RenderOptions = {
	signal?: AbortSignal;
	onProgress?: (progress: number, total: number, remainingMs: number) => void;
};

/**
 * Audio-only encoder built on Mediabunny.
 * - Supports AAC in MP4 (.m4a) or PCM in WAV (.wav).
 * - Streams audio buffers from clips in timeline order, inserting silence for gaps.
 * - Supports AudioClip, VideoClip, and ScriptLayerClip.
 * - Resamples audio to the requested sample rate/channel count.
 */
export class MbAudioEncoder {
	private composition: Composition;
	private bitrate: number;
	private format: 'm4a' | 'wav';
	private channels: 1 | 2;
	private sampleRate: 44100 | 48000;

	public constructor(composition: Composition, init?: AudioEncoderInit) {
		this.composition = composition;
		this.bitrate = init?.bitrate ?? 192e3;
		this.format = init?.format ?? 'm4a';
		this.channels = init?.channels ?? 2;
		this.sampleRate = init?.sampleRate ?? 48000;
	}

	/**
	 * Some browsers reject high AAC bitrates for specific channel/sample-rate configs.
	 * Probe support and pick the highest supported bitrate at or below `requested`.
	 */
	private async resolveSupportedAacBitrate(
		requested: number,
		numberOfChannels: number,
		sampleRate: number,
	): Promise<number> {
		const requestedBitrate = Number.isFinite(requested) && requested > 0 ? Math.round(requested) : 192000;
		try {
			const root: any = typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
			if (!root) return requestedBitrate;
			const AudioEncoderCtor: any = root.AudioEncoder;
			if (!AudioEncoderCtor || typeof AudioEncoderCtor.isConfigSupported !== 'function') return requestedBitrate;

			const preferred = [
				requestedBitrate,
				288000,
				256000,
				224000,
				192000,
				160000,
				128000,
				96000,
				64000,
			]
				.filter((v, idx, arr) => Number.isFinite(v) && v > 0 && v <= requestedBitrate && arr.indexOf(v) === idx);

			for (const bitrate of preferred) {
				try {
					const support = await AudioEncoderCtor.isConfigSupported({
						codec: 'mp4a.40.2',
						bitrate,
						numberOfChannels,
						sampleRate,
					});
					if (support?.supported) {
						const normalized = Number((support as any)?.config?.bitrate ?? bitrate);
						return Number.isFinite(normalized) && normalized > 0 ? Math.round(normalized) : bitrate;
					}
				} catch {}
			}
		} catch {}
		return requestedBitrate;
	}

	/**
	 * Collect all audio items from the composition for export
	 */
	private collectAudioItems(): AudioExportItem[] {
		const items: AudioExportItem[] = [];

		console.log(`[MbAudioEncoder] Scanning ${this.composition.tracks.length} tracks for audio...`);

		for (const tr of this.composition.tracks) {
			for (const c of tr.clips as any[]) {
				if (!c || tr.disabled || c.disabled) continue;

				// Handle AudioClip
				if (c instanceof AudioClip && c.source?.input) {
					const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
						Array.isArray((c as any).segments) ? (c as any).segments : [];
					if (Array.isArray(segs) && segs.length > 0) {
						for (const seg of segs) {
							const startSec = Number(seg?.compStartSec ?? NaN);
							const inSec = Number(seg?.srcStartSec ?? NaN);
							const outSec = Number(seg?.srcEndSec ?? NaN);
							if (!Number.isFinite(startSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
							items.push({
								startSec,
								inSec,
								outSec,
								getAudioTrack: async () => c.source.input.getPrimaryAudioTrack(),
								gain: Number(c.gain ?? 1.0) || 1.0,
								rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
								kind: 'media',
								sourceKey: `audio:${c.id}`,
							});
						}
					} else {
						const startSec = c.start?.seconds ?? 0;
						const inSec = c.range?.[0]?.seconds ?? 0;
						const outSec = c.range?.[1]?.seconds ?? (inSec + (c.duration?.seconds ?? 0));
						console.log(`[MbAudioEncoder] AudioClip: start=${startSec}, in=${inSec}, out=${outSec}`);
						items.push({
							startSec,
							inSec,
							outSec,
							getAudioTrack: async () => c.source.input.getPrimaryAudioTrack(),
							gain: Number(c.gain ?? 1.0) || 1.0,
							rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
							kind: 'media',
							sourceKey: `audio:${c.id}`,
						});
					}
					continue;
				}

				// Handle VideoClip with embedded audio
				if (c instanceof VideoClip && c.videoSource?.input) {
					const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> =
						Array.isArray((c as any).segments) ? (c as any).segments : [];
					if (Array.isArray(segs) && segs.length > 0) {
						for (const seg of segs) {
							const startSec = Number(seg?.compStartSec ?? NaN);
							const inSec = Number(seg?.srcStartSec ?? NaN);
							const outSec = Number(seg?.srcEndSec ?? NaN);
							if (!Number.isFinite(startSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
							items.push({
								startSec,
								inSec,
								outSec,
								getAudioTrack: async () => c.videoSource.input.getPrimaryAudioTrack(),
								gain: Number(c.gain ?? 1.0) || 1.0,
								rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
								kind: 'media',
								sourceKey: `video:${c.id}`,
							});
						}
					} else {
						const startSec = c.start?.seconds ?? 0;
						const inSec = c.range?.[0]?.seconds ?? 0;
						const outSec = c.range?.[1]?.seconds ?? (inSec + (c.duration?.seconds ?? 0));
						console.log(`[MbAudioEncoder] VideoClip: start=${startSec}, in=${inSec}, out=${outSec}`);
						items.push({
							startSec,
							inSec,
							outSec,
							getAudioTrack: async () => c.videoSource.input.getPrimaryAudioTrack(),
							gain: Number(c.gain ?? 1.0) || 1.0,
							rate: Number((c as any).playbackRate ?? 1.0) || 1.0,
							kind: 'media',
							sourceKey: `video:${c.id}`,
						});
					}
					continue;
				}

				// Handle ScriptLayerClip - can have multiple audio sources
				if (isScriptClip(c)) {
					const audioEntries = c.getAudioEntries();
					const segments = (c as any).scriptSegments ?? [];
					console.log(`[MbAudioEncoder] ScriptLayerClip with ${audioEntries.length} audio entries, segments=${segments?.length ?? 0}`);

					if (Array.isArray(segments) && segments.length > 0 && Array.isArray(audioEntries) && audioEntries.length > 0) {
						const clipGain = Number((c as any).gain ?? 1.0) || 1.0;
						type Placement = { offsetSec: number; inSec: number; outSec: number; rate: number; endOnClip: number };
						const EPS = 1e-3;

						for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
							const entry: any = audioEntries[sourceIndex];
							if (!entry?.input) continue;
                            const speakerMuteRanges = Array.isArray((c as any)?.speakerMuteRanges)
                                ? ((c as any).speakerMuteRanges as any[])
                                : [];

							const entryGain = Number(entry.gain ?? 1.0) || 1.0;
							const baseGain = entryGain * clipGain;
							const placements: Placement[] = [];
							const entrySegs: any[] | undefined =
								Array.isArray(entry?.segments) && entry.segments.length > 0 ? entry.segments : undefined;

							if (entrySegs) {
								for (const s of entrySegs) {
									const offsetSec = Number(s?.offsetSec ?? NaN);
									const inSec = Number(s?.range?.inSec ?? NaN);
									const outSec = Number(s?.range?.outSec ?? NaN);
									const rate = Number(s?.playbackRate ?? entry?.playbackRate ?? 1.0) || 1.0;
									if (!Number.isFinite(offsetSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) continue;
									if (!Number.isFinite(rate) || rate <= 0) continue;
									const endOnClip = offsetSec + (outSec - inSec) / rate;
									placements.push({ offsetSec, inSec, outSec, rate, endOnClip });
								}
							} else {
								const offsetSec = Number(entry?.offsetSec ?? 0);
								const inSec = Number(entry?.range?.inSec ?? NaN);
								const outSec = Number(entry?.range?.outSec ?? NaN);
								const rate = Number(entry?.playbackRate ?? 1.0) || 1.0;
								if (Number.isFinite(offsetSec) && Number.isFinite(inSec) && Number.isFinite(outSec) && outSec > inSec && Number.isFinite(rate) && rate > 0) {
									const endOnClip = offsetSec + (outSec - inSec) / rate;
									placements.push({ offsetSec, inSec, outSec, rate, endOnClip });
								}
							}

							if (placements.length === 0) continue;

							for (const seg of segments as any[]) {
								const segStart = Number(seg?.srcStartSec ?? NaN);
								const segEnd = Number(seg?.srcEndSec ?? NaN);
								const segCompStart = Number(seg?.compStartSec ?? NaN);
                                const segRateRaw = Number(seg?.playbackRate ?? (c as any).playbackRate ?? 1.0);
                                const segRate = Number.isFinite(segRateRaw) && segRateRaw > 0 ? segRateRaw : 1.0;
								if (!Number.isFinite(segStart) || !Number.isFinite(segEnd) || segEnd <= segStart) continue;
								if (!Number.isFinite(segCompStart) || seg?.isGap) continue;

								let segSceneGainAdjust = 1.0;
								let segSourceGainMult = 1.0;
								try {
									const mid = (segStart + segEnd) / 2;
									const sceneIndex =
										typeof (c as any).findSceneIndexForTime === "function"
											? Number((c as any).findSceneIndexForTime(mid))
											: -1;
									if (Number.isFinite(sceneIndex) && sceneIndex >= 0) {
										const sceneProps =
											typeof (c as any).getSceneProperties === "function"
												? (c as any).getSceneProperties(sceneIndex)
												: (c as any).sceneProperties?.get?.(sceneIndex);
										const sceneGain = Number((sceneProps as any)?.gain);
										if (Number.isFinite(sceneGain) && clipGain > 0 && sceneGain !== clipGain) {
											segSceneGainAdjust = sceneGain / clipGain;
										}
										const audioSourceGains = (sceneProps as any)?.audioSourceGains;
										const multRaw =
											audioSourceGains && typeof audioSourceGains.get === "function"
												? Number(audioSourceGains.get(sourceIndex))
												: NaN;
										if (Number.isFinite(multRaw)) segSourceGainMult = multRaw;
									}
								} catch {}

								const overlaps: Array<{ start: number; end: number; p: Placement }> = [];
								for (const p of placements) {
									const o0 = Math.max(segStart, p.offsetSec);
									const o1 = Math.min(segEnd, p.endOnClip);
									if (o1 <= o0 + EPS) continue;
									overlaps.push({ start: o0, end: o1, p });
								}
								overlaps.sort((a, b) => a.start - b.start);

								for (const ov of overlaps) {
									const os = ov.start;
									const oe = ov.end;
									if (oe <= os + EPS) continue;

                                    const audiblePieces = splitTimelineRangeBySpeakerMutes({
                                        startSec: os,
                                        endSec: oe,
                                        sourceIndex,
                                        muteRanges: speakerMuteRanges,
                                    });
                                    if (audiblePieces.length === 0) continue;

									for (const piece of audiblePieces) {
										const ps = Number(piece.startSec ?? NaN);
										const pe = Number(piece.endSec ?? NaN);
										if (!Number.isFinite(ps) || !Number.isFinite(pe) || pe <= ps + EPS) continue;

										const timelineStart = segCompStart + (ps - segStart) / segRate;
										const srcStart = ov.p.inSec + (ps - ov.p.offsetSec) * ov.p.rate;
										const srcEnd = ov.p.inSec + (pe - ov.p.offsetSec) * ov.p.rate;
										const gain = baseGain * segSceneGainAdjust * segSourceGainMult;
										items.push({
											startSec: timelineStart,
											inSec: srcStart,
											outSec: srcEnd,
											getAudioTrack: async () => entry.input.getPrimaryAudioTrack(),
											gain,
											kind: 'media',
											rate: ov.p.rate * segRate,
											sourceKey: `script:${c.id}:src${sourceIndex}`,
										});
									}
								}
							}
						}
						continue;
					}

					// Fallback: legacy, no script segments available.
                    const speakerMuteRanges = Array.isArray((c as any)?.speakerMuteRanges)
                        ? ((c as any).speakerMuteRanges as any[])
                        : [];
					for (let i = 0; i < audioEntries.length; i++) {
						const entry: any = audioEntries[i];
						if (!entry?.input) continue;

						const clipStartSec = c.start?.seconds ?? 0;
                        const sceneRateRaw = Number((c as any).playbackRate ?? 1.0);
                        const sceneRate = Number.isFinite(sceneRateRaw) && sceneRateRaw > 0 ? sceneRateRaw : 1.0;
						const entryOffsetSec = Number(entry.offsetSec ?? 0);
						const inSec = Number(entry.range?.inSec ?? 0);
						let outSec = entry.range?.outSec;
						if (outSec === undefined) {
							const clipDur = c.duration?.seconds;
							outSec = clipDur ? (inSec + clipDur) : (inSec + 60);
						}
                        const sourceRate = Number(entry.playbackRate ?? 1.0) || 1.0;
                        const rate = sourceRate * sceneRate;
                        const clipLocalStart = entryOffsetSec;
                        const clipLocalEnd = clipLocalStart + Math.max(0, (outSec - inSec) / Math.max(0.001, sourceRate));
                        const pieces = splitTimelineRangeBySpeakerMutes({
                            startSec: clipLocalStart,
                            endSec: clipLocalEnd,
                            sourceIndex: i,
                            muteRanges: speakerMuteRanges,
                        });
                        for (const piece of pieces) {
                            const pieceStartOffset = Number(piece.startSec ?? 0) - clipLocalStart;
                            const pieceEndOffset = Number(piece.endSec ?? 0) - clipLocalStart;
                            if (pieceEndOffset <= pieceStartOffset) continue;
                            items.push({
                                startSec: clipStartSec + Number(piece.startSec ?? 0) / sceneRate,
                                inSec: inSec + pieceStartOffset * sourceRate,
                                outSec: inSec + pieceEndOffset * sourceRate,
                                getAudioTrack: async () => entry.input.getPrimaryAudioTrack(),
                                gain: Number(entry.gain ?? 1.0) || 1.0,
                                kind: 'media',
                                rate,
                                sourceKey: `script:${c.id}:src${i}`,
                            });
                        }
					}
					continue;
				}
			}
		}

		// Sort by timeline position
		items.sort((a, b) => a.startSec - b.startSec);
		
		console.log(`[MbAudioEncoder] Total audio items collected: ${items.length}`);

		return items;
	}

	public async render(target: FileSystemFileHandle | string = 'audio.m4a', opts?: RenderOptions): Promise<void> {
		const signal = opts?.signal;
		const onProgress = opts?.onProgress;

		const mbTarget = await createMbTarget(target);

		const format = this.format === 'wav' ? new WavOutputFormat() : new Mp4OutputFormat();
		const audioCodecForContainer = this.format === 'wav' ? 'pcm-s16' as const : 'aac' as const;

		const output = new Output({ format, target: mbTarget.target });

		// Prepare audio track
		let resolvedBitrate = this.bitrate;
		if (audioCodecForContainer === 'aac') {
			resolvedBitrate = await this.resolveSupportedAacBitrate(
				this.bitrate,
				this.channels,
				this.sampleRate,
			);
			if (resolvedBitrate !== this.bitrate) {
				console.warn(
					`[MbAudioEncoder] Requested AAC bitrate ${this.bitrate} not supported; falling back to ${resolvedBitrate}.`
				);
			}
		}
		const audioSource = audioCodecForContainer === 'aac'
			? new AudioBufferSource({
				codec: audioCodecForContainer,
				bitrate: resolvedBitrate,
			})
			: new AudioBufferSource({
				codec: audioCodecForContainer,
			});
		output.addAudioTrack(audioSource);

		// Collect audio items from all clip types
		const items = this.collectAudioItems();
		
		if (items.length === 0) {
			console.warn('[MbAudioEncoder] No audio items found in composition');
		}

		const totalSec = this.composition.duration?.seconds ?? items.reduce((acc, it) => Math.max(acc, it.startSec + Math.max(0, it.outSec - it.inSec)), 0);

			try {
				try {
					await output.start();
					await writeMixedAudioTrack({
						audioSource,
						items,
						durationSec: totalSec,
						targetChannels: this.channels,
						targetSampleRate: this.sampleRate,
						signal,
					});
					if (signal?.aborted) {
						await output.cancel();
						throw new DOMException('User aborted rendering', 'AbortError');
					}
					if (onProgress) {
						onProgress(Math.floor(totalSec * 1000), Math.floor((totalSec || 1) * 1000), 0);
					}
					console.log(`[MbAudioEncoder] Export complete, total: ${totalSec.toFixed(2)}s`);

			await output.finalize();

					// Save/upload output if needed.
					const buf = (output.target as any).buffer as ArrayBuffer | null | undefined;
					const mime =
						format instanceof WavOutputFormat
							? 'audio/wav'
							: format instanceof Mp4OutputFormat
								? 'audio/mp4'
								: 'audio/webm';
					await (mbTarget as any).finalize?.({ buffer: buf ?? null }, mime);
				} catch (e) {
					// If we were streaming to OPFS, make sure we don't leave large temp files behind on failure/cancel.
					try {
						await (mbTarget as any).cleanup?.();
					} catch {}
					throw e;
				}
			} finally {
				// nothing to reset for audio-only
			}
	}
}
